import { createContext, useContext, useState, useEffect } from 'react';
import api from '../utils/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);      // super_admin
  const [mosque, setMosque] = useState(null);  // mosque admin
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('zq_token');
    const role = localStorage.getItem('zq_role');
    if (!token) { setLoading(false); return; }

    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

    if (role === 'super_admin') {
      const stored = JSON.parse(localStorage.getItem('zq_user') || '{}');
      setUser(stored);
    } else if (role === 'mosque') {
      const stored = JSON.parse(localStorage.getItem('zq_mosque') || '{}');
      const storedPlan = JSON.parse(localStorage.getItem('zq_plan') || 'null');
      setMosque(stored);
      setPlan(storedPlan);
    }
    setLoading(false);
  }, []);

  const loginSuperAdmin = async (email, password) => {
    const { data } = await api.post('/auth/super-admin/login', { email, password });
    localStorage.setItem('zq_token', data.token);
    localStorage.setItem('zq_role', 'super_admin');
    localStorage.setItem('zq_user', JSON.stringify(data.user));
    api.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;
    setUser(data.user);
  };

  const loginMosque = async (email, password) => {
    const { data } = await api.post('/auth/mosque/login', { email, password });
    localStorage.setItem('zq_token', data.token);
    localStorage.setItem('zq_role', 'mosque');
    localStorage.setItem('zq_mosque', JSON.stringify(data.mosque));
    localStorage.setItem('zq_plan', JSON.stringify(data.plan));
    api.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;
    setMosque(data.mosque);
    setPlan(data.plan);
    return data;
  };

  const isViewer = mosque?.subAdminRole === 'viewer';

  const logout = async () => {
    const role = localStorage.getItem('zq_role');
    if (role === 'mosque') {
      await api.post('/auth/mosque/logout').catch(() => {});
    }
    localStorage.removeItem('zq_token');
    localStorage.removeItem('zq_role');
    localStorage.removeItem('zq_user');
    localStorage.removeItem('zq_mosque');
    localStorage.removeItem('zq_plan');
    delete api.defaults.headers.common['Authorization'];
    setUser(null);
    setMosque(null);
    setPlan(null);
  };

  const hasFeature = (feature) => plan?.features?.[feature] === true;

  const refreshPlan = async () => {
    try {
      const { data } = await api.get('/admin/profile');
      const freshPlan = data.mosque?.plan
        ? {
            plan: data.mosque.plan,
            is_trial: data.mosque.is_trial,
            expires_at: data.mosque.expires_at,
            features: data.mosque.features || {},
            max_competitors: data.mosque.max_competitors,
            max_admins: data.mosque.max_admins,
          }
        : null;
      setPlan(freshPlan);
      localStorage.setItem('zq_plan', JSON.stringify(freshPlan));
    } catch {
      // silently fail — plan stays as-is
    }
  };

  return (
    <AuthContext.Provider value={{
      user, mosque, plan, loading,
      loginSuperAdmin, loginMosque, logout, hasFeature, refreshPlan,
      isSuperAdmin: !!user,
      isMosqueAdmin: !!mosque,
      isAuthenticated: !!(user || mosque),
      isViewer,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
